import { lazy, Suspense } from 'react';
import { RouterProvider } from 'react-router';

import { Toaster } from '@cenorion/ui/components/sonner';

import { AppProviders } from '@/app/app-providers';
import { isDev } from '@/core/env/env';
import { useDirection } from '@/core/i18n/use-direction';
import { queryClient } from '@/core/query/query-client';
import { usePreferencesStore } from '@/core/stores/preferences-store';

import type { createAppRouter } from '@/app/router';

/**
 * Provider stack, above the router: protected data fetching is gated by the `requireAuth`
 * loader, not by provider placement, so every route (public or protected) can use them.
 */
// Dynamic import keeps devtools out of the production bundle; a static import inside an
// isDev conditional would remove only the render, not the bytes.
const Devtools = lazy(async () => {
  const mod = await import('@tanstack/react-query-devtools');
  return { default: mod.ReactQueryDevtools };
});

// Injected rather than imported: importing a constructed router here would build it during
// module evaluation, before bootstrap() settles the session and cleans the OAuth callback URL.
type AppProps = { router: ReturnType<typeof createAppRouter> };

export function App({ router }: AppProps) {
  const dir = useDirection();
  const theme = usePreferencesStore((state) => state.theme);

  return (
    <AppProviders queryClient={queryClient} direction={dir}>
      <RouterProvider router={router} />
      {/* Sonner's `position` is literal left/right, not logical start/end, so RTL needs an
          explicit flip to keep toasts anchored to the same reading-order corner as LTR. */}
      <Toaster theme={theme} dir={dir} position={dir === 'rtl' ? 'top-left' : 'top-right'} />
      {isDev && (
        <Suspense fallback={null}>
          <Devtools initialIsOpen={false} />
        </Suspense>
      )}
    </AppProviders>
  );
}

export default App;
